import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Building2, MapPin, Phone, Search, Filter, X, PhoneCall, Mail, ChevronDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { ThemeToggle } from '../components/ThemeToggle';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_agency-network-hub/artifacts/d66f2cg2_IMG_0019.png";

const AGENCY_TYPES = [
  { value: 'home_care', label: 'Home Care' },
  { value: 'respite_hospice', label: 'Respite / Hospice' },
  { value: 'residential_nursing', label: 'Residential / Nursing Home' },
  { value: 'clinics', label: 'Clinics' },
  { value: 'hospital', label: 'Hospital' },
];

const CARE_LEVELS = [
  { value: 'companionship', label: 'Companionship' },
  { value: 'light_house_duty', label: 'Light House Duty / Cleaning' },
  { value: 'personal_care', label: 'Personal Care' },
  { value: 'wound_care', label: 'Wound Care' },
  { value: 'medication_administration', label: 'Medication Administration' },
  { value: 'palliative_care', label: 'Palliative Care' },
];

export function BrowseAgencies() {
  const [agencies, setAgencies] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedCareLevel, setSelectedCareLevel] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(true);
  const [callbackModal, setCallbackModal] = useState(null);
  const [callbackForm, setCallbackForm] = useState({ name: '', phone: '', email: '', message: '', care_level: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchAgencies();
  }, [selectedType, selectedCareLevel]);

  const fetchAgencies = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedType) params.append('agency_type', selectedType);
      if (selectedCareLevel) params.append('care_level', selectedCareLevel);
      const { data } = await axios.get(`${API_URL}/api/public/organizations?${params.toString()}`);
      setAgencies(data);
    } catch (err) {
      console.error('Failed to fetch agencies:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredAgencies = agencies.filter(agency =>
    agency.name?.toLowerCase().includes(search.toLowerCase()) ||
    agency.address?.toLowerCase().includes(search.toLowerCase())
  );

  // Group agencies by type
  const groupedAgencies = {};
  AGENCY_TYPES.forEach(t => { groupedAgencies[t.value] = []; });
  groupedAgencies['other'] = [];
  filteredAgencies.forEach(agency => {
    const type = agency.agency_type || 'other';
    if (groupedAgencies[type]) {
      groupedAgencies[type].push(agency);
    } else {
      groupedAgencies['other'].push(agency);
    }
  });

  const handleCallback = async (e) => {
    e.preventDefault();
    if (!callbackForm.name || !callbackForm.phone) {
      toast.error('Name and phone number are required');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/public/callback-request`, {
        organization_id: callbackModal.id,
        ...callbackForm
      });
      toast.success('Callback request sent! The agency will contact you soon.');
      setCallbackModal(null);
      setCallbackForm({ name: '', phone: '', email: '', message: '', care_level: '' });
    } catch (err) {
      toast.error('Failed to send callback request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const clearFilters = () => {
    setSelectedType('');
    setSelectedCareLevel('');
    setSearch('');
  };

  const hasActiveFilters = selectedType || selectedCareLevel || search;
  const showGrouped = !selectedType && !search;

  const typeColors = {
    home_care: '#00A6A0',
    respite_hospice: '#FF6F61',
    residential_nursing: '#4A90E2',
    clinics: '#A8E6CF',
    hospital: '#F97316',
    other: '#94A3B8'
  };

  const renderAgencyCard = (agency) => (
    <Card key={agency.id} className="glass-card glass-card-hover group" data-testid={`agency-card-${agency.id}`}>
      <CardContent className="p-6">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${typeColors[agency.agency_type] || '#94A3B8'}15` }}>
            <Building2 className="w-6 h-6" style={{ color: typeColors[agency.agency_type] || '#94A3B8' }} />
          </div>
          <div className="min-w-0">
            <h3 className="text-lg font-heading font-semibold text-foreground truncate">
              {agency.name}
            </h3>
            {agency.agency_type_label && (
              <span className="inline-block text-xs px-2 py-0.5 rounded-full mt-1"
                style={{
                  backgroundColor: `${typeColors[agency.agency_type] || '#94A3B8'}15`,
                  color: typeColors[agency.agency_type] || '#94A3B8'
                }}>
                {agency.agency_type_label}
              </span>
            )}
          </div>
        </div>

        {agency.description && (
          <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{agency.description}</p>
        )}

        <div className="space-y-1.5 mb-4">
          {agency.address && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate">{agency.address}</span>
            </div>
          )}
          {agency.phone && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Phone className="w-3.5 h-3.5 flex-shrink-0" />
              <span>{agency.phone}</span>
            </div>
          )}
        </div>

        {agency.care_level_labels?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-4">
            {agency.care_level_labels.map((label, i) => (
              <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                {label}
              </span>
            ))}
          </div>
        )}

        <Button
          onClick={() => setCallbackModal(agency)}
          className="w-full btn-primary"
          data-testid={`callback-btn-${agency.id}`}
        >
          <PhoneCall className="w-4 h-4 mr-2" />
          Request a Callback
        </Button>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-background relative">
      <div className="glass-blobs"><div className="blob-3" /></div>
      <div className="noise-overlay" />

      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="FendanaCura" className="h-8 sm:h-9 w-auto" />
              <span className="text-lg sm:text-xl font-heading font-bold text-foreground">FendanaCura</span>
            </Link>
            <div className="hidden md:flex items-center gap-6">
              <Link to="/jobs" className="text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-jobs">
                Find Jobs
              </Link>
              <Link to="/register-company" className="text-muted-foreground hover:text-foreground transition-colors">
                Register Agency
              </Link>
            </div>
            <div className="flex items-center gap-2">
              <ThemeToggle />
              <Link to="/login">
                <Button variant="outline" className="border-[#00A6A0] text-[#00A6A0] hover:bg-[#00A6A0]/10 font-medium text-xs sm:text-sm px-3 sm:px-4" data-testid="sign-in-btn">Sign In</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-12 relative z-10">
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-4">
            Browse Care Agencies
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto text-base sm:text-lg">
            Find the right care agency for you or your loved ones. Filter by type and level of care.
          </p>
        </div>

        {/* Search & Filter Bar */}
        <div className="max-w-3xl mx-auto mb-8 space-y-4">
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or location..."
                className="w-full pl-12 py-6 bg-card border-border"
                data-testid="agency-search-input"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={`btn-secondary py-6 px-4 ${hasActiveFilters ? 'border-[#00A6A0] text-[#00A6A0]' : ''}`}
              data-testid="toggle-filters-btn"
            >
              <Filter className="w-5 h-5" />
              {hasActiveFilters && <span className="ml-1 w-2 h-2 rounded-full bg-[#00A6A0]" />}
            </Button>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="glass-card rounded-xl p-6 space-y-5 animate-in slide-in-from-top-2" data-testid="filter-panel">
              <div className="flex items-center justify-between">
                <h3 className="font-heading font-semibold text-foreground">Filters</h3>
                {hasActiveFilters && (
                  <button onClick={clearFilters} className="text-sm text-[#00A6A0] hover:underline flex items-center gap-1" data-testid="clear-filters-btn">
                    <X className="w-3 h-3" /> Clear all
                  </button>
                )}
              </div>

              <div>
                <Label className="text-sm text-muted-foreground mb-2 block">Agency Type</Label>
                <div className="flex flex-wrap gap-2">
                  {AGENCY_TYPES.map(type => (
                    <button
                      key={type.value}
                      onClick={() => setSelectedType(selectedType === type.value ? '' : type.value)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                        selectedType === type.value
                          ? 'bg-[#00A6A0] text-white'
                          : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                      data-testid={`filter-type-${type.value}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-sm text-muted-foreground mb-2 block">Level of Care</Label>
                <div className="flex flex-wrap gap-2">
                  {CARE_LEVELS.map(level => (
                    <button
                      key={level.value}
                      onClick={() => setSelectedCareLevel(selectedCareLevel === level.value ? '' : level.value)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                        selectedCareLevel === level.value
                          ? 'bg-[#4A90E2] text-white'
                          : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                      data-testid={`filter-care-${level.value}`}
                    >
                      {level.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Results */}
        {isLoading ? (
          <div className="text-center py-16">
            <div className="spinner mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading agencies...</p>
          </div>
        ) : filteredAgencies.length === 0 ? (
          <div className="text-center py-16">
            <Building2 className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
              No Agencies Found
            </h3>
            <p className="text-muted-foreground mb-4">
              {hasActiveFilters ? 'Try adjusting your filters' : 'No agencies are registered yet'}
            </p>
            {hasActiveFilters && (
              <Button variant="outline" onClick={clearFilters} className="btn-secondary" data-testid="clear-search-btn">
                Clear Filters
              </Button>
            )}
          </div>
        ) : showGrouped ? (
          /* Grouped by type */
          <div className="space-y-10">
            {AGENCY_TYPES.map(type => {
              const group = groupedAgencies[type.value];
              if (!group?.length) return null;
              return (
                <div key={type.value} data-testid={`agency-group-${type.value}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: typeColors[type.value] }} />
                    <h2 className="text-lg font-heading font-semibold text-foreground">{type.label}</h2>
                    <span className="text-sm text-muted-foreground">({group.length})</span>
                  </div>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {group.map(renderAgencyCard)}
                  </div>
                </div>
              );
            })}
            {groupedAgencies['other']?.length > 0 && (
              <div data-testid="agency-group-other">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-3 h-3 rounded-full bg-muted-foreground" />
                  <h2 className="text-lg font-heading font-semibold text-foreground">Other</h2>
                  <span className="text-sm text-muted-foreground">({groupedAgencies['other'].length})</span>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {groupedAgencies['other'].map(renderAgencyCard)}
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Flat list when search/filter active */
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredAgencies.map(renderAgencyCard)}
          </div>
        )}
      </div>

      {/* Callback Request Modal */}
      {callbackModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="callback-modal">
          <div className="glass-card rounded-2xl p-8 max-w-md w-full relative">
            <button onClick={() => setCallbackModal(null)} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground">
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-xl font-heading font-bold text-foreground mb-1">Request a Callback</h2>
            <p className="text-sm text-muted-foreground mb-6">
              From <span className="text-[#00A6A0] font-medium">{callbackModal.name}</span>
            </p>
            <form onSubmit={handleCallback} className="space-y-4">
              <div>
                <Label className="text-foreground">Your Name *</Label>
                <Input
                  value={callbackForm.name}
                  onChange={(e) => setCallbackForm({ ...callbackForm, name: e.target.value })}
                  placeholder="Full name"
                  className="bg-card border-border mt-1"
                  required
                  data-testid="callback-name-input"
                />
              </div>
              <div>
                <Label className="text-foreground">Phone Number *</Label>
                <Input
                  value={callbackForm.phone}
                  onChange={(e) => setCallbackForm({ ...callbackForm, phone: e.target.value })}
                  placeholder="+44 7xxx xxx xxx"
                  className="bg-card border-border mt-1"
                  required
                  data-testid="callback-phone-input"
                />
              </div>
              <div>
                <Label className="text-foreground">Email (optional)</Label>
                <Input
                  type="email"
                  value={callbackForm.email}
                  onChange={(e) => setCallbackForm({ ...callbackForm, email: e.target.value })}
                  placeholder="you@example.com"
                  className="bg-card border-border mt-1"
                  data-testid="callback-email-input"
                />
              </div>
              <div>
                <Label className="text-foreground">Care Level Needed</Label>
                <select
                  value={callbackForm.care_level}
                  onChange={(e) => setCallbackForm({ ...callbackForm, care_level: e.target.value })}
                  className="w-full mt-1 rounded-md border border-border bg-card text-foreground px-3 py-2 text-sm"
                  data-testid="callback-care-level-select"
                >
                  <option value="">Select care level...</option>
                  {CARE_LEVELS.map(l => (
                    <option key={l.value} value={l.value}>{l.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-foreground">Message (optional)</Label>
                <textarea
                  value={callbackForm.message}
                  onChange={(e) => setCallbackForm({ ...callbackForm, message: e.target.value })}
                  placeholder="Tell us about your care needs..."
                  rows={3}
                  className="w-full mt-1 rounded-md border border-border bg-card text-foreground px-3 py-2 text-sm resize-none"
                  data-testid="callback-message-input"
                />
              </div>
              <Button type="submit" disabled={submitting} className="w-full btn-primary py-5" data-testid="callback-submit-btn">
                {submitting ? 'Sending...' : 'Send Callback Request'}
              </Button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default BrowseAgencies;
